import React, { useState } from 'react'
import { User, Lock, Eye, EyeOff } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import api from '../services/api'
import './Settings.css'

const PASSWORD_DOTS = '••••••••'

const Settings = () => {
  const { user } = useAuth()
  const [verifyPassword, setVerifyPassword] = useState('')
  const [verifyModalOpen, setVerifyModalOpen] = useState(false)
  const [verifyLoading, setVerifyLoading] = useState(false)
  const [verifyError, setVerifyError] = useState('')
  const [revealedPassword, setRevealedPassword] = useState(null)

  const username = user?.name ?? ''
  const passwordVisible = revealedPassword !== null

  const handleRevealClick = () => {
    if (passwordVisible) return
    setVerifyModalOpen(true)
    setVerifyError('')
    setVerifyPassword('')
  }

  const handleHideClick = () => {
    setRevealedPassword(null)
  }

  const handleVerifySubmit = async (e) => {
    e.preventDefault()
    setVerifyError('')
    setVerifyLoading(true)
    try {
      const res = await api.post('/auth/verify-password', { password: verifyPassword })
      setRevealedPassword(res.password ?? verifyPassword)
      setVerifyModalOpen(false)
      setVerifyPassword('')
    } catch (err) {
      setVerifyError(err.response?.data?.message || 'Incorrect password')
    } finally {
      setVerifyLoading(false)
    }
  }

  return (
    <div className="settings-page">
      <div className="page-header">
        <div className="header-titles">
          <h1>Settings</h1>
          <p>Configure dashboard preferences and account security.</p>
        </div>
      </div>

      <div className="settings-container">
        <div className="settings-content">
          <div className="settings-section">
            <h2>Profile Information</h2>
            <p className="section-description">Your account profile</p>

            <div className="form-group">
              <label>Username</label>
              <div className="profile-field profile-username">
                <User size={18} />
                <span className="profile-value">{username || '—'}</span>
              </div>
            </div>

            <div className="form-group">
              <label>Password</label>
              <div className="profile-field profile-password">
                <Lock size={18} />
                <span className="profile-value profile-password-value">
                  {passwordVisible ? revealedPassword : PASSWORD_DOTS}
                </span>
                {passwordVisible ? (
                  <button
                    type="button"
                    className="btn-reveal-password"
                    onClick={handleHideClick}
                    title="Hide password"
                  >
                    <EyeOff size={18} />
                    <span>Hide</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn-reveal-password"
                    onClick={handleRevealClick}
                    title="Enter your password to verify and view"
                  >
                    <Eye size={18} />
                    <span>Reveal</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {verifyModalOpen && (
        <div className="modal-overlay" onClick={() => setVerifyModalOpen(false)}>
          <div className="modal-content profile-verify-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Enter your password to view</h3>
            <p className="modal-description">Enter your current password to reveal the password field.</p>
            <form onSubmit={handleVerifySubmit}>
              {verifyError && <div className="error-message">{verifyError}</div>}
              <div className="form-group">
                <label>Current password</label>
                <input
                  type="password"
                  className="input"
                  value={verifyPassword}
                  onChange={(e) => setVerifyPassword(e.target.value)}
                  placeholder="Enter password"
                  autoFocus
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setVerifyModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={verifyLoading || !verifyPassword}>
                  {verifyLoading ? 'Verifying…' : 'Verify'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default Settings
